<template>
  <div :style="{ display: 'contents' }">
    <!-- Cann Group -->
    <div
      ref="cann-group-name"
      :style="cannGroupHeaderStyle"
      class="grid-item-border-bottom flexed-white-bg-grid-item header cann-group-heading-container"
      :class="cgHeaderPositioning"
    >
      <div
        class="d-flex flex-row align-center pa-2 full-width"
        :style="{ cursor: cannGroupCollapseEnabled ? 'pointer' : 'default' }"
        @click="toggleCannGroup"
      >
        <div
          class="inside-box-title flex-grow-1 d-flex justify-center"
          :style="{ maxWidth: cannGroupIsOpen ? 'fit-content' : '370px' }"
        >
          <div
            class="title-text"
            :class="cannGroupIsOpen ? 'text-left' : 'text-center'"
            :style="cannGroupIsOpen ? {} : { width: '100%' }"
          >
            <h2
              :key="`cann-group-name-${isCollapsed}`"
              :title="getCannGroupName"
              class="cann-group-name"
            >
              {{ getCannGroupName }}
            </h2>
            <div v-if="showNotImplemented" class="d-flex cann-group-detail">
              {{ $t('assortmentCanvasesPanel.percentOfSpace', ['x']) }}
            </div>
          </div>
        </div>
        <span
          v-if="cannGroupCollapseEnabled"
          class="box-icon d-flex flex-shrink-0 ml-auto"
          :class="isCollapsed ? 'mdi mdi-plus-box' : 'mdi mdi-minus-box'"
        />
      </div>
    </div>
    <!-- CDT -->
    <div
      v-if="!isCollapsed"
      ref="cdt-name"
      :style="{ 'grid-column': `span ${childColumns}`, top: `${cannGroupHeaderHeight}px` }"
      class="grid-item-border-bottom flexed-white-bg-grid-item header"
      :class="cdtHeaderPositioning"
    >
      <div class="d-flex flex-row full-width flex-grow-1 justify-center grey-background pa-1">
        <div class="inside-box-title">
          <div class="title-text">
            <h2 :title="getCdtName" class="header-large">
              {{ getCdtName }}
            </h2>
          </div>
        </div>
      </div>
    </div>
    <!-- Here we render the roots nodes (no parents) in the cdt node tree -->
    <template v-if="!isCollapsed">
      <CdtColumn
        v-for="cdtNode in cdtRoots"
        :key="cdtNode._id"
        :cdt-node-id="cdtNode._id"
        :cann-group-id="scenarioCdt.cannGroupId"
        :depth="1"
        :cdts="scenarioCdt.cdt"
        :child-tree="childTree"
        :previous-heights="accumulatedHeights"
        @child-nodes-expanded="sumChildNodes"
        @child-nodes-collapsed="removeChildNodes"
      />
    </template>
    <!-- When collapsed we render all products in a single flat column -->
    <template v-else>
      <TileView
        v-if="selectedView === views.Tile"
        :key="`collapsed-tile-${scenarioCdt.cannGroupId}`"
        :products="allCannGroupProducts"
        :group-name="scenarioCdt.cannGroupId"
      />
      <ListView
        v-else
        :products="allCannGroupProducts"
        :group-name="scenarioCdt.cannGroupId"
        :previous-heights="cannGroupHeaderHeight"
        :positioning="cdtHeaderPositioning"
      />
    </template>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useContextStore } from '@/stores/context';
import { useScenariosStore } from '@/stores/scenarios';
import { useFeature } from '@/composables/use-feature';
import { useCannGroupProducts } from '@/composables/use-cann-group-products';
import destroy from '@/utils/destroy';
import assortmentCanvases from '@enums/assortment-canvases';
import { filter, get, keyBy, mapValues, omit, sum, values } from 'lodash';
import { mapActions, mapState } from 'pinia';

export default {
  name: 'CannGroupColumn',
  props: {
    scenarioCdt: {
      required: true,
      type: Object,
    },
  },

  setup(props) {
    const { cannGroupCollapseEnabled } = useFeature();
    const cannGroupId = computed(() => props.scenarioCdt.cannGroupId);
    const { allCannGroupProducts, childTree } = useCannGroupProducts(cannGroupId);
    return { cannGroupCollapseEnabled, allCannGroupProducts, childTree };
  },

  data() {
    return {
      openLeafNodes: {},
      cannGroupHeaderHeight: null,
      cdtHeaderHeight: null,
      isCollapsed: false,
      views: assortmentCanvases.Views,
    };
  },

  computed: {
    ...mapState(useAssortmentCanvasStore, [
      'stickyHeaders',
      'selectedView',
      'totalExpandedHeaderDepth',
    ]),
    ...mapState(useScenariosStore, ['cannGroups', 'attributesById']),
    ...mapState(useContextStore, ['showNotImplemented']),

    childColumns() {
      if (this.isCollapsed) return 1;
      // This calculation works out how many columns each grid item needs to span. This is calculating the open leaf nodes at the bottom of its respective tree.
      // This is set in the template above for each column e.g 'grid-column: span {childColumns}'
      const unexpandedChildren = this.cdtRoots.filter(x => !this.openLeafNodes[x._id]);
      return sum(values(this.openLeafNodes)) + unexpandedChildren.length;
    },

    cannGroupHeaderStyle() {
      const base = { 'grid-column': `span ${this.childColumns}`, top: 0 };
      if (this.isCollapsed) {
        // Span all CDT header rows so the grid stays aligned with expanded cann groups.
        base['grid-row'] = `span ${1 + this.totalExpandedHeaderDepth * 2}`;
      }
      return base;
    },

    getCannGroupName() {
      const matchingCannGroup = this.cannGroups.find(cg => cg.key === this.scenarioCdt.cannGroupId);
      return get(matchingCannGroup, 'name', '');
    },

    getCdtName() {
      const cdtName = this.scenarioCdt.cdt[0].attributeId;
      // hack to avoid undefined issues on testing env
      return get(this.attributesById, cdtName, { name: this.$t('general.notFound') }).name;
    },

    cdtRoots() {
      // Calculate all root nodes, i.e the children that have no parentId in our cdt tree.
      return filter(this.scenarioCdt.cdt, { parentId: null });
    },

    accumulatedHeights() {
      // We are calculating the heights of the cannGroupHeader and cdtNameHeader via refs. We need to do this to pass it down to the cdt-column component as a prop.
      // This helps to calculate the offsetHeights of each element so we can set the sticky headers programatically.
      return this.cannGroupHeaderHeight + this.cdtHeaderHeight;
    },

    cgHeaderPositioning() {
      // If user has stickyHeaders option enabled then set their position as sticky.
      return this.stickyHeaders ? 'sticky' : 'relative';
    },

    cdtHeaderPositioning() {
      return this.stickyHeaders ? 'sticky' : '';
    },

    cannGroupIsOpen() {
      return this.cannGroupCollapseEnabled && !this.isCollapsed;
    },
  },

  created() {
    this.setChildTrees();
    const keyedRoots = keyBy(this.cdtRoots, '_id');
    this.openLeafNodes = mapValues(keyedRoots, () => 1);
    if (this.cannGroupCollapseEnabled) {
      this.isCollapsed = true;
    }
  },

  beforeUnmount() {
    destroy.destroyReactiveVueProps(this);
  },

  mounted() {
    this.getHeaderHeights();
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, ['setChildTree', 'resetCannGroupExpandedCdts']),

    setChildTrees() {
      // TODO: refactor this so it's on the ac store and returns a full tree that can cache
      this.setChildTree({ id: this.scenarioCdt.cannGroupId, tree: this.childTree });
    },

    sumChildNodes(nodeObject) {
      // When the 'child-nodes-expanded' event is sent from the new-cdt-column component, update the openLeafNodes data object.
      this.openLeafNodes = { ...this.openLeafNodes, ...nodeObject };
    },

    removeChildNodes(nodeId) {
      // Remove the child node count for a certain nodeId, this event is emitted in new-cdt-column when a node is collapsed.
      this.openLeafNodes = omit(this.openLeafNodes, nodeId);
    },

    getHeaderHeights() {
      this.cannGroupHeaderHeight = this.$refs['cann-group-name'].offsetHeight;
      if (this.$refs['cdt-name']) {
        this.cdtHeaderHeight = this.$refs['cdt-name'].offsetHeight;
      }
    },

    toggleCannGroup() {
      if (!this.cannGroupCollapseEnabled) return;
      this.isCollapsed = !this.isCollapsed;
      if (this.isCollapsed) {
        // Reset openLeafNodes back to initial state (one per root node).
        const keyedRoots = keyBy(this.cdtRoots, '_id');
        this.openLeafNodes = mapValues(keyedRoots, () => 1);
        // Clean up expandedCdts in the store so totalExpandedHeaderDepth recalculates correctly.
        this.resetCannGroupExpandedCdts(Object.keys(this.childTree));
        // Force a layout reflow so text-overflow: ellipsis applies correctly after collapsing.
        this.$nextTick(() => this.getHeaderHeights());
      } else {
        // Re-read header heights once the CDT rows are rendered.
        this.$nextTick(() => this.getHeaderHeights());
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.cann-group-heading-container {
  border-top: 1px solid $assortment-tile-border-colour;
}

.icons-container {
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  right: 0;

  &.compressed {
    padding-right: 6px;
    flex-direction: row-reverse;
    padding-left: 5px;
    width: 100%;
    margin-right: 0 !important;
    justify-content: space-between;
  }
}

.header-large {
  padding-left: 10px;
  padding-right: 10px;
  white-space: nowrap;
  height: fit-content;
  font-size: 1.2rem;
  text-overflow: ellipsis;
  overflow: hidden;
}

.box-icon {
  font-size: 2.3rem !important;
  align-self: flex-start;
  font-weight: bold;
  color: $assortment-action-icon-color !important;
}

.cann-group-name {
  white-space: nowrap;
  padding-left: 30px;
  padding-right: 30px;
  font-size: 1.4rem;
  text-overflow: ellipsis;
  overflow: hidden;
}

.cann-group-detail {
  justify-content: center;
  font-size: 1.2rem;
}

.draggable-cross {
  font-size: 1.9rem !important;
  font-weight: bold;
  color: $assortment-action-icon-color !important;
}

.grey-background {
  background-color: $canvas-cdt-header-background;
}
</style>
